import React, { useEffect, useState } from "react";
import Client from "shopify-buy";
import { ShoppingBag, Bell, ExternalLink, Loader2, Lock } from "lucide-react";

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_TOKEN;
const COLLECTION_ID = process.env.REACT_APP_SHOPIFY_COLLECTION_ID; // optional

const isShopifyConfigured = Boolean(DOMAIN && TOKEN);

function ProductCard({ product, onAdd, adding }) {
  const variant = product.variants?.[0];
  const img = product.images?.[0]?.src;
  const price = variant?.price?.amount
    ? `$${parseFloat(variant.price.amount).toFixed(2)}`
    : "—";

  return (
    <article
      data-testid={`merch-product-${product.id}`}
      className="glass-card group relative overflow-hidden flex flex-col"
    >
      {/* Image */}
      <div className="relative aspect-square bg-black overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={product.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[800ms]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/30">
            <ShoppingBag size={32} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      {/* Meta */}
      <div className="p-4 md:p-5 flex-1 flex flex-col">
        <h3 className="font-display font-bold text-base md:text-lg text-cream-soft leading-tight line-clamp-2">
          {product.title}
        </h3>
        <div className="mt-1 font-mono-display text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">
          {product.productType || "Merch"}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="font-display font-bold text-lg text-[#FF00E5] text-glow-magenta">
            {price}
          </span>
          <button
            data-testid={`merch-add-${product.id}`}
            disabled={adding || !variant?.available}
            onClick={() => onAdd(variant.id)}
            className="btn-shine px-3 py-2 bg-[#FF00E5] hover:bg-[#FF00E5]/90 text-black font-mono-display text-[10px] uppercase tracking-[0.2em] font-bold disabled:opacity-50 disabled:cursor-not-allowed glow-magenta transition-all flex items-center gap-1.5"
          >
            {adding ? (
              <><Loader2 size={11} className="animate-spin" /> Adding</>
            ) : !variant?.available ? (
              <><Lock size={11} /> Sold Out</>
            ) : (
              "Add to Cart"
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyShopifyPlaceholder() {
  const scrollToMosh = () => document.getElementById("mosh")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div className="reveal">
      <div className="relative glass-card p-10 md:p-16 lg:p-20 text-center overflow-hidden border-[#FF00E5]/20">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-[#FF00E5]/15 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-[#00F0FF]/10 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 synth-grid opacity-30 pointer-events-none" aria-hidden="true" />

        <div className="relative z-10 max-w-xl mx-auto">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 border border-[#FF00E5]/40 bg-black/60 glow-magenta">
            <ShoppingBag size={22} className="text-[#FF00E5]" />
          </div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-cream-soft leading-tight">
            The first drop is in the lab.
          </h3>
          <p className="mt-4 text-white/65 text-sm md:text-base max-w-md mx-auto">
            Hoodies, patches, stickers, and tees designed around the album. Mosh Team gets first crack —
            24 hours before public release.
          </p>
          <button
            data-testid="merch-notify-button"
            onClick={scrollToMosh}
            className="btn-shine relative mt-8 px-7 py-3.5 border border-[#FF00E5] bg-[#FF00E5]/10 hover:bg-[#FF00E5]/20 text-[#FF00E5] hover:text-cream-soft font-mono-display text-xs uppercase tracking-[0.3em] transition-all flex items-center gap-2 mx-auto glow-magenta"
          >
            <Bell size={13} /> Notify Me · Join Mosh Team
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Merch() {
  const [client] = useState(() => {
    if (!isShopifyConfigured) return null;
    return Client.buildClient({
      domain: DOMAIN,
      storefrontAccessToken: TOKEN,
      apiVersion: "2024-10",
    });
  });

  const [products, setProducts] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(isShopifyConfigured);
  const [error, setError] = useState(null);
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    if (!client) return;
    // Init checkout
    client.checkout.create().then(setCheckout).catch(() => {});
    // Fetch products (collection if provided, else first 20 products)
    const fetcher = COLLECTION_ID
      ? client.collection.fetchWithProducts(COLLECTION_ID, { productsFirst: 20 }).then((c) => c.products)
      : client.product.fetchAll(20);

    fetcher
      .then((items) => setProducts(items || []))
      .catch((e) => setError(e?.message || "Could not load products"))
      .finally(() => setLoading(false));
  }, [client]);

  const onAdd = async (variantId) => {
    if (!client || !checkout) return;
    setAddingId(variantId);
    try {
      const updated = await client.checkout.addLineItems(checkout.id, [
        { variantId, quantity: 1 },
      ]);
      setCheckout(updated);
      // Open Shopify checkout in new tab
      window.open(updated.webUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      // noop — user can retry
    } finally {
      setAddingId(null);
    }
  };

  return (
    <section id="merch" data-testid="merch-section" className="relative py-24 md:py-32 px-5 md:px-10 max-w-7xl mx-auto">
      <div className="text-center mb-12 reveal">
        <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#00F0FF]">
          05 — Drops
        </span>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 text-cream-soft">
          MERCH <span className="text-[#FF00E5] text-glow-magenta">SHOP</span>
        </h2>
        {isShopifyConfigured && (
          <p className="mt-4 text-white/60 text-sm md:text-base">
            Wear the album. Checkout secured by Shopify.
          </p>
        )}
      </div>

      {!isShopifyConfigured ? (
        <EmptyShopifyPlaceholder />
      ) : loading ? (
        <div className="flex items-center justify-center py-20 text-white/55">
          <Loader2 size={20} className="animate-spin mr-3" />
          <span className="font-mono-display text-xs uppercase tracking-[0.3em]">Loading drops…</span>
        </div>
      ) : error ? (
        <div className="text-center py-12 text-white/55 font-mono-display text-xs">
          {error}
        </div>
      ) : products.length === 0 ? (
        <EmptyShopifyPlaceholder />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={onAdd} adding={addingId === p.variants?.[0]?.id} />
            ))}
          </div>

          {checkout?.webUrl && (
            <div className="mt-10 text-center">
              <a
                data-testid="merch-checkout-link"
                href={checkout.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#00F0FF]/60 text-[#00F0FF] hover:bg-[#00F0FF]/10 font-mono-display text-xs uppercase tracking-[0.3em] hover:glow-cyan transition-all"
              >
                <ShoppingBag size={13} /> View Cart · Checkout
                <ExternalLink size={11} />
              </a>
            </div>
          )}
        </>
      )}
    </section>
  );
}
